import { useEffect, useState } from 'react';
import type { ErrorLogEntry } from '@sflx/shared';
import { errorLog } from '../lib/api';

const POLL_MS = 10_000;
/** Roughly two minutes of backed-off retries before the poll gives up. */
const MAX_POLL_FAILURES = 12;
/** An error counts as "just happened" for the dashboard badge inside this window. */
const RECENT_WINDOW_S = 10 * 60;

export interface RecentErrorsState {
  errors: ErrorLogEntry[];
  /** How many of `errors` happened within the last ten minutes — what the badge counts. */
  recentCount: number;
}

/**
 * Poll the error log while `enabled` — the read endpoint is admin-gated, so a
 * viewer polling it would just collect 403s. Same chained-timeout-with-backoff
 * shape as `useIngestProgress`: a slow response must not stack requests, and a
 * single failed poll must not kill the dashboard indicator for good.
 */
export function useRecentErrors(enabled: boolean): RecentErrorsState {
  const [errors, setErrors] = useState<ErrorLogEntry[]>([]);

  useEffect(() => {
    if (!enabled) {
      setErrors([]);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let failures = 0;

    const tick = async () => {
      try {
        const result = await errorLog.list(20);
        if (cancelled) return;
        setErrors(result.items);
        failures = 0;
        timer = setTimeout(() => void tick(), POLL_MS);
      } catch {
        if (cancelled) return;
        failures += 1;
        if (failures > MAX_POLL_FAILURES) return;
        timer = setTimeout(() => void tick(), POLL_MS * Math.min(failures, 8));
      }
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled]);

  const now = Math.floor(Date.now() / 1000);
  const recentCount = errors.filter((entry) => now - entry.createdAt < RECENT_WINDOW_S).length;

  return { errors, recentCount };
}
